import React from 'react';
import { Chip, ChipProps } from '@mui/material';
import type { SparePart, Tool } from '../../types/inventory';

type SparePartStatus = SparePart['status'];
type ToolStatus = Tool['status'];
type ToolCondition = Tool['condition'];

interface StatusBadgeProps {
  type: 'spare-part' | 'tool-status' | 'tool-condition';
  value: SparePartStatus | ToolStatus | ToolCondition;
  size?: ChipProps['size'];
}

const getSparePartConfig = (status: SparePartStatus) => {
  switch (status) {
    case 'available':
      return { label: 'Verfügbar', color: 'success' as const };
    case 'low_stock':
      return { label: 'Niedrig', color: 'warning' as const };
    case 'out_of_stock':
      return { label: 'Ausverkauft', color: 'error' as const };
    case 'discontinued':
      return { label: 'Eingestellt', color: 'default' as const };
    default:
      return { label: status, color: 'default' as const };
  }
};


const getToolStatusConfig = (status: ToolStatus) => {
  switch (status) {
    case 'available':
      return { label: 'Verfügbar', color: 'success' as const };
    case 'in_use':
      return { label: 'In Benutzung', color: 'primary' as const };
    case 'maintenance':
      return { label: 'Wartung', color: 'warning' as const };
    case 'missing':
      return { label: 'Vermisst', color: 'error' as const };
    default:
      return { label: status, color: 'default' as const };
  }
};

const getToolConditionConfig = (condition: ToolCondition) => {
  switch (condition) {
    case 'excellent':
      return { label: 'Ausgezeichnet', color: 'success' as const };
    case 'good':
      return { label: 'Gut', color: 'info' as const };
    case 'fair':
      return { label: 'Akzeptabel', color: 'warning' as const };
    case 'poor':
      return { label: 'Schlecht', color: 'error' as const };
    default:
      return { label: condition, color: 'default' as const };
  }
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({ type, value, size = 'small' }) => {
  let config: { label: string; color: ChipProps['color'] };

  switch (type) {
    case 'spare-part':
      config = getSparePartConfig(value as SparePartStatus);
      break;
    case 'tool-status':
      config = getToolStatusConfig(value as ToolStatus);
      break;
    case 'tool-condition':
      config = getToolConditionConfig(value as ToolCondition);
      break;
    default:
      config = { label: value as string, color: 'default' };
  }

  return (
    <Chip
      label={config.label}
      color={config.color}
      size={size}
      sx={{
        fontWeight: 500,
        minWidth: '80px',
      }}
    />
  );
};
